import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface UserMetrics {
  trailsCompleted: number;
  milesCompleted: number;
  runsThisMonth: number;
  totalRuns: number;
  recentRuns: any[];
}

export const useUserMetrics = (limit: number = 5) => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<UserMetrics>({
    trailsCompleted: 0,
    milesCompleted: 0,
    runsThisMonth: 0,
    totalRuns: 0,
    recentRuns: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    fetchMetrics();
  }, [user?.id, limit]);

  const fetchMetrics = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const userId = user?.id;
      if (!userId) return;

      // Get total runs count and total miles
      const { data: runsData, error: runsError } = await supabase
        .from('runs')
        .select('distance_miles, start_time')
        .eq('user_id', userId);

      if (runsError) throw runsError;

      // Get runs this month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const runsThisMonth = runsData?.filter(run => {
        const dt = run.start_time ? new Date(run.start_time) : null;
        return dt !== null && dt >= startOfMonth;
      }).length || 0;

      // Calculate total miles
      const totalMiles = runsData?.reduce((sum, run) => 
        sum + (run.distance_miles || 0), 0
      ) || 0;

      // Get unique trails completed count
      const { data: segmentsData, error: segmentsError } = await supabase
        .from('user_segments')
        .select('trail_segment_id')
        .eq('user_id', userId)
        .eq('status', 'completed');

      if (segmentsError) throw segmentsError;

      const uniqueTrailsCount = new Set(
        segmentsData?.map(segment => segment.trail_segment_id) || []
      ).size;

      // Get recent runs for activity feed
      const { data: recentRunsData, error: recentRunsError } = await supabase
        .from('runs')
        .select(`
          id,
          title,
          distance_miles,
          equipment_type,
          start_time,
          created_at,
          processing_status,
          run_segments(
            trail_segment_id,
            trail_segment:trail_segments(
              trail_name,
              difficulty
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (recentRunsError) throw recentRunsError;

      setMetrics({
        trailsCompleted: uniqueTrailsCount,
        milesCompleted: Math.round(totalMiles * 100) / 100,
        runsThisMonth,
        totalRuns: runsData?.length || 0,
        recentRuns: recentRunsData || []
      });
    } catch (err) {
      console.error('Error fetching user metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setIsLoading(false);
    }
  };

  return { metrics, isLoading, error, refetch: fetchMetrics };
};